/**
 * Convert verified text links (including Markdown book links from ONE CORE)
 * into safe, clickable anchors in the chat UI.
 *
 * This is intentionally not a generic HTML renderer.
 */
export type ChatLinkToken = { kind: "text"; text: string } | { kind: "link"; text: string; href: string };

function safeHttpUrl(raw: string): string | undefined {
  try {
    const parsed = new URL(raw);
    if (!["https:", "http:"].includes(parsed.protocol) || parsed.username || parsed.password) return undefined;
    if (parsed.href.length > 4096) return undefined;
    return parsed.href;
  } catch {
    return undefined;
  }
}

function labelForUrl(href: string): string {
  try { return new URL(href).hostname.replace(/^www\./, ""); }
  catch { return "Abrir fuente"; }
}

export function tokenizeChatLinks(text: string): ChatLinkToken[] {
  const result: ChatLinkToken[] = [];
  const links = /\[([^\]\r\n]{1,100})\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>()\]]+)/giu;
  let last = 0;
  for (const match of text.matchAll(links)) {
    const start = match.index;
    if (start > last) result.push({ kind: "text", text: text.slice(last, start) });
    const raw = match[2] ?? match[3];
    const href = safeHttpUrl(raw);
    if (href) result.push({ kind: "link", text: match[1] ?? labelForUrl(href), href });
    else result.push({ kind: "text", text: match[0] });
    last = start + match[0].length;
  }
  if (last < text.length) result.push({ kind: "text", text: text.slice(last) });
  return result;
}

/**
 * Legacy UI safety: no completed PDF can be claimed solely in the text.
 * A real download must be backed by a verifiable file attachment.
 */
export function truthfulChatMessage(content: string): string {
  if (/^\s*(?:✅|📄|✔️)?\s*(?:archivo|documento)\s+(?:PDF|DOCX?|EPUB|TXT|ZIP)\s+(?:creado|generado|preparado)(?:\s+y\s+verificado)?\b/iu.test(content)) {
    return "No hay un archivo descargable verificado adjunto a esta respuesta. " +
      "OMNISYNIA debe generar el documento y confirmar su enlace real antes de anunciarlo como creado.";
  }
  return content;
}
